import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hammer, Plus, Search } from "lucide-react";

import { api } from "@/lib/api";
import { Badge, ChipRow, Empty, ErrorView, Loading, SearchInput } from "@/components/kit";
import { C } from "@/lib/format";

export const pct = (v) => (v === null || v === undefined ? "—" : `${String(v).replace(".", ",")} %`);

const CITIES = [
  { key: "all", label: "Toute la France" }, { key: "59", label: "Lille" }, { key: "33", label: "Bordeaux" },
  { key: "69", label: "Lyon" }, { key: "13", label: "Marseille" }, { key: "75", label: "Paris" },
  { key: "31", label: "Toulouse" }, { key: "44", label: "Nantes" }, { key: "06", label: "Nice" },
];

export function MasterCard({ m, onClick, selected, testID }) {
  return (
    <button
      type="button"
      data-testid={testID}
      aria-pressed={selected}
      onClick={onClick}
      className={`pressable flex w-full flex-col gap-1 rounded-[20px] border p-4 text-left ${selected ? "border-[#D4AF37] bg-[#292415] ring-1 ring-[#D4AF37]" : "border-[#333333] bg-[#1E1E1E]"}`}
    >
      <span className="flex w-full items-center gap-2">
        <span className="t-h3 min-w-0 flex-1 truncate">{m.company_name}</span>
        {m.commission_rate !== null && m.commission_rate !== undefined ? <span className="t-caption !text-[#D4AF37]">≈ {pct(m.commission_rate)} indicatif</span> : null}
      </span>
      <span className="t-caption w-full truncate">{m.city} ({m.department}) · {m.region}</span>
      <span className="t-caption w-full truncate">
        Couvre : {m.departments_covered.join(", ")}
        {m.specialties.length ? ` · ${m.specialties.join(", ")}` : ""}
      </span>
      <span className="mt-1 flex flex-wrap items-center gap-2">
        <Badge label={m.insurance_ok ? "Assurances vérifiées" : "Assurances à vérifier"} color={m.insurance_ok ? C.success : C.warning} />
        {m.status === "inactive" && <Badge label="Désactivé" color={C.silver} />}
        {m.projects !== undefined && <span className="t-caption">{m.projects} projet(s) · {m.signed} signé(s)</span>}
      </span>
    </button>
  );
}

export function MastersList({ initialDept }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState(initialDept || "all");
  useEffect(() => {
    if (initialDept) setDept(initialDept);
  }, [initialDept]);
  const options = CITIES.some((c) => c.key === dept) ? CITIES : [CITIES[0], { key: dept, label: `Dép. ${dept}` }, ...CITIES.slice(1)];
  const term = search.trim();
  const q = useQuery({
    queryKey: ["admin-masters", term, dept],
    queryFn: () => api(`/admin/masters?q=${encodeURIComponent(term)}${dept !== "all" ? `&department=${dept}` : ""}`),
    placeholderData: (prev) => prev,
  });
  return (
    <div data-testid="admin-masters-list" className="flex flex-col">
      <div className="border-b border-[#222222] px-4 md:px-8">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[240px] max-w-xl flex-1">
            <SearchInput icon={Search} testID="admin-masters-search-input" value={search} onChange={setSearch} placeholder="Entreprise, ville, département, spécialité…" />
          </div>
          <button
            type="button"
            data-testid="admin-masters-add-button"
            onClick={() => navigate("/admin/master-form")}
            className="focus-ring fixed bottom-20 right-4 z-20 flex h-14 items-center gap-2 rounded-full bg-[#D4AF37] px-5 font-bold text-[#121212] shadow-xl transition-colors hover:bg-[#E2C25A] lg:static lg:h-11 lg:rounded-xl lg:shadow-none"
          >
            <Plus className="h-[22px] w-[22px]" /> Ajouter
          </button>
        </div>
        <ChipRow options={options} value={dept} onChange={setDept} testIDPrefix="admin-masters-city" />
      </div>
      {q.isLoading ? <Loading /> : q.isError ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 p-4 pb-28 md:p-8 lg:pb-8">
          <p className="t-caption" data-testid="admin-masters-count">
            {q.data?.length ?? 0} maître(s) d'œuvre{dept !== "all" ? ` couvrant le ${dept}` : ""}
          </p>
          {q.data?.length ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {q.data.map((m) => <MasterCard key={m.id} m={m} testID={`admin-master-item-${m.id}`} onClick={() => navigate(`/admin/master/${m.id}`)} />)}
            </div>
          ) : (
            <Empty
              icon={Hammer}
              title={term || dept !== "all" ? "Aucun résultat" : "Aucun maître d'œuvre"}
              text={term || dept !== "all" ? "Aucun maître d'œuvre ne couvre cette recherche : une région à renforcer." : "Ajoutez vos maîtres d'œuvre partenaires avec le bouton « Ajouter »."}
            />
          )}
        </div>
      )}
    </div>
  );
}
